/**
 * JAPAP — i18n configuration (iter72, 11-language coverage)
 * ==========================================================
 * All 11 supported languages ship a full UI bundle. The list is the
 * single source of truth exported from `constants/languages.js` and
 * mirrored on the backend in `constants.py::SUPPORTED_LANGS`.
 *
 * Priority order for picking the active language:
 *   1. `preferred_lang` on the authenticated user (synced in AuthContext)
 *   2. `localStorage['japap_ui_lang']`
 *   3. browser `navigator.language`
 *   4. fallback: English
 *
 * RTL languages (Arabic) automatically flip the `dir` attribute on
 * `<html>` via the `languageChanged` listener below.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import axios from 'axios';

import en from './locales/en.json';

import { SUPPORTED_CODES, isRTL } from './constants/languages';

// Perf — only English (fallback) ships in the main bundle. Every other UI
// language is a separate chunk fetched on demand (detected language at
// boot, or on changeLanguage). i18next keeps showing the previous
// language until the new bundle is loaded, so raw keys never flash.
const LOCALE_LOADERS = {
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr.json'),
  pt: () => import(/* webpackChunkName: "locale-pt" */ './locales/pt.json'),
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es.json'),
  ar: () => import(/* webpackChunkName: "locale-ar" */ './locales/ar.json'),
  sw: () => import(/* webpackChunkName: "locale-sw" */ './locales/sw.json'),
  ln: () => import(/* webpackChunkName: "locale-ln" */ './locales/ln.json'),
  yo: () => import(/* webpackChunkName: "locale-yo" */ './locales/yo.json'),
  hi: () => import(/* webpackChunkName: "locale-hi" */ './locales/hi.json'),
  bn: () => import(/* webpackChunkName: "locale-bn" */ './locales/bn.json'),
  ta: () => import(/* webpackChunkName: "locale-ta" */ './locales/ta.json'),
  // iter238 — Russian (minimal Paystack + common). Strictly additive.
  ru: () => import(/* webpackChunkName: "locale-ru" */ './locales/ru.json'),
};

const lazyLocaleBackend = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    const load = LOCALE_LOADERS[language];
    if (!load) { callback(null, {}); return; }
    load()
      .then((mod) => callback(null, mod.default || mod))
      .catch((err) => callback(err, null));
  },
};

export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_CODES,
    // i18next is picky about regional codes (pt-PT, fr-CA…). Strip them.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      // iter203 — Detection priority for ANONYMOUS users (logged-in users
      // are still overridden by `preferred_lang` from AuthContext) :
      //   1. localStorage (explicit user choice — sticky across sessions)
      //   2. navigator (browser/system language → covers most native users)
      //   3. htmlTag (server-rendered <html lang> — useful for SEO)
      //   4. fallbackLng: 'fr' (historical default)
      // Geo-IP detection happens via /api/geo/detect on first load (see
      // useGeoLanguageBootstrap hook) and writes into localStorage, so it
      // wins over navigator on the second page.
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      // iter268 — QA shortcut: ?_lang=en|fr|es|ar overrides everything else
      // for the current tab — invaluable when verifying the EN experience
      // before a Bitcointalk-style launch without changing the user's
      // sticky preference.
      lookupQuerystring: '_lang',
      caches: ['localStorage'],
      lookupLocalStorage: 'japap_ui_lang',
    },
  });

// Flip <html lang/dir> on every language change. Done here so the effect
// is shared by every surface (app, marketing page, auth page) without
// each component re-implementing it.
function applyHtmlLang(lng) {
  try {
    const short = String(lng || 'en').toLowerCase().slice(0, 2);
    document.documentElement.setAttribute('lang', short);
    document.documentElement.setAttribute('dir', isRTL(short) ? 'rtl' : 'ltr');
    // Server error messages are localised from this header.
    axios.defaults.headers.common['X-App-Lang'] = short;
  } catch (_) {}
}
applyHtmlLang(i18n.language);
i18n.on('languageChanged', applyHtmlLang);

// iter272-fe — Bootstrap IP-based fallback (4th priority) for first-time
// anonymous visitors. Runs once per browser session — writes into localStorage
// so it becomes sticky. Explicit user choice (via QA querystring, sidebar
// picker, or profile) always wins because they update localStorage first.
async function bootstrapGeoLang() {
  try {
    if (typeof window === 'undefined') return;
    // Already have a persisted choice → do nothing (respects user's pick).
    if (localStorage.getItem('japap_ui_lang')) return;
    // Already had a navigator match that's one of our 14 langs → do nothing.
    const nav = String((navigator.language || 'en')).toLowerCase().slice(0, 2);
    if (SUPPORTED_CODES.includes(nav)) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    if (!backend) return;
    const res = await fetch(`${backend}/api/geo/detect-lang`, { method: 'GET' });
    if (!res.ok) return;
    const j = await res.json();
    const lg = String(j.language || 'en').toLowerCase().slice(0, 2);
    if (SUPPORTED_CODES.includes(lg) && lg !== i18n.language) {
      await i18n.changeLanguage(lg);
    }
  } catch { /* silent — 'en' fallback remains */ }
}
// Fire and forget after initial paint.
if (typeof window !== 'undefined') {
  setTimeout(bootstrapGeoLang, 0);
}

export default i18n;
